// Import getParamValue from the leaf module (not the `@/util` barrel): `@/util`
// imports the store, so importing it here would be a store → plugin → util cycle.
import { getParamValue } from '@/utils/url.utils'

const MARKETPLACE_TYPE_PREFIX = 'marketplace/'
// The same body class Utils.disallowPDFRender / allowPDFRender toggle; the PDF
// exporter waits (up to 60s) for it to clear before each screenshot.
const PDF_RENDER_WAITING_CLASS = 'waiting'
// Covers the Unleash load plus the catalogue fetch's full retry budget (3 × 8s + 1.5s
// backoff), and still leaves room under the exporter's 60s wait for the embed's own
// 12s fallback (35 + 12 = 47s).
export const MARKETPLACE_CAPTURE_HOLD_MS = 35000

export const unrenderableMarketplaceSlideType = (state, getters) => {
  const type = state.slideActive?.type
  if (typeof type !== 'string' || !type.startsWith(MARKETPLACE_TYPE_PREFIX)) return null
  const isResolved = !!getters['embedApp/customSlideTypeByType'](type)
  return isResolved && state.isSharingSlideStageMounted ? null : type
}

// A `marketplace/` slide only renders its plugin embed once the marketplace
// catalogue has loaded and the lazily imported sharing stage has mounted; until
// then nothing on the stage sets `body.waiting`, so a capture would screenshot an
// empty slide. Hold the flag across that gap.
export const createMarketplaceCaptureRenderHoldPlugin = ({
  isCapture = () => getParamValue('capture', window.location.href) === 'true',
  holdRender = () => document.body.classList.add(PDF_RENDER_WAITING_CLASS),
  releaseRender = () => document.body.classList.remove(PDF_RENDER_WAITING_CLASS),
  holdMs = MARKETPLACE_CAPTURE_HOLD_MS,
} = {}) => (store) => {
  if (!isCapture()) return

  let heldType = null
  let releaseTimer = null

  const cancelReleaseTimer = () => {
    if (releaseTimer) clearTimeout(releaseTimer)
    releaseTimer = null
  }

  store.watch(unrenderableMarketplaceSlideType, (unrenderableType) => {
    cancelReleaseTimer()
    if (unrenderableType) {
      heldType = unrenderableType
      holdRender()
      releaseTimer = setTimeout(() => {
        releaseTimer = null
        heldType = null
        releaseRender()
      }, holdMs)
      return
    }
    if (heldType === null) return
    // Renderable in place: the plugin embed now mounting owns the flag, so leave it set.
    const renderableInPlace = store.state.slideActive?.type === heldType
    heldType = null
    if (!renderableInPlace) releaseRender()
  }, { immediate: true })
}

export default createMarketplaceCaptureRenderHoldPlugin()
